import { useProduct } from "../hooks/core/useProduct";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { 
  ArrowRight, 
  Sparkles, 
  CheckCircle2,
  Shield,
  Database,
  Plus
} from "lucide-react";
import useSites from "../hooks/features/sites/useSites";

export default function ProductSelectPage() {
  const { selectedProduct, changeProduct } = useProduct();
  const navigate = useNavigate();
  const [hoveredProduct, setHoveredProduct] = useState<string | null>(null);
  const { sites: products, loading } = useSites();

  // Transformer les sites en format produit
  const productList = products.map(site => ({
    value: site.slug,
    label: site.name,
    logo: site.logo || '/default-logo.png',
  }));

  const handleProductSelect = (productValue: string) => {
    changeProduct(productValue);
    navigate("/admin/dashboard");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-gray-200 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-gray-200 flex items-center justify-center p-4">
      <div className="w-full max-w-6xl">
        {/* En-tête */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full shadow-sm border border-gray-200/60 mb-6">
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="text-xs font-medium text-gray-600 uppercase tracking-wider">
              Kafintech Group INC.
            </span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 tracking-tight">
            Choisissez votre
            <span className="bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent ml-2">
              Produit
            </span>
          </h1>
          
          <p className="text-gray-500 max-w-2xl mx-auto text-sm md:text-base">
            Sélectionnez le produit que vous souhaitez administrer. 
            Vous pourrez changer à tout moment depuis votre tableau de bord.
          </p>
        </div>

        {/* Grille des produits */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {/* 🆕 Carte "Ajouter un site" */}
          <button
            onClick={() => navigate("/new-product")}
            className="group relative flex flex-col items-center p-6 rounded-2xl 
              bg-white border-2 border-dashed border-gray-300 
              hover:border-primary hover:bg-primary/5
              transition-all duration-300 text-center
              hover:shadow-xl hover:-translate-y-1
              min-h-[280px] justify-center"
          >
            <div className="w-20 h-20 rounded-xl bg-gray-100 flex items-center justify-center mb-4 group-hover:bg-primary/10 transition-colors">
              <Plus className="w-10 h-10 text-gray-400 group-hover:text-primary transition-colors" />
            </div>
            <h3 className="text-lg font-bold text-gray-400 group-hover:text-primary transition-colors">
              Ajouter un site
            </h3>
            <p className="text-sm text-gray-400 mt-1">
              Créer un nouveau produit
            </p>
          </button>

          {/* Produits existants */}
          {productList.map((product) => {
            const isHovered = hoveredProduct === product.value;

            return (
              <button
                key={product.value}
                onClick={() => handleProductSelect(product.value)}
                onMouseEnter={() => setHoveredProduct(product.value)}
                onMouseLeave={() => setHoveredProduct(null)}
                className={`
                  group relative flex flex-col items-center p-6 rounded-2xl 
                  bg-white border-2 transition-all duration-300 text-center
                  ${selectedProduct === product.value 
                    ? 'border-primary shadow-lg shadow-primary/10 scale-[1.02]' 
                    : 'border-gray-200/80 hover:border-gray-300 hover:shadow-xl hover:-translate-y-1'
                  }
                  ${isHovered ? 'shadow-xl -translate-y-1' : ''}
                `}
              >
                {/* Badge "Sélectionné" */}
                {selectedProduct === product.value && (
                  <div className="absolute -top-2 -right-2 bg-primary text-white rounded-full p-1.5 shadow-lg shadow-primary/30">
                    <CheckCircle2 className="w-4 h-4" />
                  </div>
                )}

                {/* Logo du produit */}
                <div className={`
                  w-20 h-20 rounded-xl flex items-center justify-center mb-4
                  bg-white border border-gray-200
                  shadow-sm transition-transform duration-300 p-3
                  ${isHovered ? 'scale-110' : ''}
                `}>
                  <img 
                    src={product.logo} 
                    alt={`Logo ${product.label}`}
                    className="w-full h-full object-contain"
                  />
                </div>

                {/* Nom du produit */}
                <h3 className="text-lg font-bold text-gray-900 mb-1.5 group-hover:text-primary transition-colors">
                  {product.label}
                </h3>

                {/* Indicateurs de fonctionnalités */}
                <div className="flex items-center gap-3 text-xs text-gray-400 w-full border-t border-gray-100 pt-3 mt-auto justify-center">
                  <div className="flex items-center gap-1">
                    <Shield className="w-3 h-3" />
                    <span>Sécurisé</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Database className="w-3 h-3" />
                    <span>Données</span>
                  </div>
                </div>

                {/* Flèche d'action */}
                <div className={`
                  absolute bottom-6 right-6 
                  transition-all duration-300
                  ${isHovered ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-2'}
                `}>
                  <div className="p-1.5 rounded-full bg-primary/10 text-primary">
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </div>

                {/* Effet de brillance au survol */}
                {isHovered && (
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shimmer" />
                )}
              </button>
            );
          })}
        </div>

        {/* Message de produit actif */}
        {selectedProduct && (
          <div className="mt-8 text-center">
            <div className="inline-flex items-center gap-3 bg-white/90 backdrop-blur-sm px-6 py-3 rounded-full shadow-sm border border-gray-200/60">
              <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-sm text-gray-600">
                Produit actif : <span className="font-semibold text-gray-900">
                  {productList.find(p => p.value === selectedProduct)?.label || selectedProduct}
                </span>
              </span>
              <span className="w-px h-4 bg-gray-200" />
              <button
                onClick={() => navigate("/dashboard")}
                className="text-sm font-medium text-primary hover:text-primary/80 transition-colors flex items-center gap-1"
              >
                Accéder au tableau de bord
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-12 text-center">
          <p className="text-[10px] text-gray-400 uppercase tracking-wider">
            © {new Date().getFullYear()} Kafintech Group INC. Tous droits réservés.
          </p>
        </div>
      </div>

      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .animate-shimmer {
          animation: shimmer 1.5s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}